import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Pageable } from 'app/interfaces/Pageable/pageable';
import { FaturaTrStatus } from 'app/interfaces/enum/fatura-tr-status.enum';
import { FaturaTr } from 'app/interfaces/fatura-tr';
import { FaturaTrDesagrupar } from 'app/interfaces/fatura-tr-desagrupar';
import { FaturaProcessoTrAbaResultado } from 'app/interfaces/processo-tr-aba-resultado';
import { RateioValoresTr } from 'app/interfaces/rateio-valores-tr';
import { SepararCustosTr } from 'app/interfaces/separar-custos-tr';
import { TimezoneService } from 'app/utils/timezone.service';
import { environment } from 'environments/environment';

@Injectable({
  providedIn: 'root',
})
export class FaturaTrHttpService {
  constructor(
    private _http: HttpClient,
    private _timezoneService: TimezoneService
  ) {}

  getAll({
    filtro = '',
    produtos = ['TR'],
    administrativa = false,
    faturaNumero = '',
    processo = '',
    sacadoNome = '',
    familiaSacadoNome = '',
    dataVencimentoInicial = '',
    dataVencimentoFinal = '',
    situacaoFatura = [],
    formaCobranca = [],
    dataDesembarqueInicial = '',
    dataDesembarqueFinal = '',
    dataEmbarqueInicial = '',
    dataEmbarqueFinal = '',
    valorFatura = '',
    moedaId = '',
    vendedorNome = '',
    statusConferencia = '',
    tipoFatura = ['PROCESSOS'],
    naturezaFatura = [],
    funcaoParceiroSacadoNome = '',
    page = 0,
    size = 500,
    sort = 'faturaTrId,desc',
  }) {
    const params = {
      filtro: filtro,
      produtos: produtos.join(','),
      administrativa: administrativa,
      faturaNumero: faturaNumero,
      processo: processo,
      sacadoNome: sacadoNome ? sacadoNome : '',
      familiaSacadoNome: familiaSacadoNome ? familiaSacadoNome : '',
      dataVencimentoInicial: dataVencimentoInicial,
      dataVencimentoFinal: dataVencimentoFinal,
      situacaoFatura: situacaoFatura.join(','),
      formaCobranca: formaCobranca.join(','),
      dataDesembarqueInicial: dataDesembarqueInicial,
      dataDesembarqueFinal: dataDesembarqueFinal,
      dataEmbarqueInicial: dataEmbarqueInicial,
      dataEmbarqueFinal: dataEmbarqueFinal,
      valorFatura: valorFatura ? valorFatura : '',
      moedaId: moedaId ? moedaId : '',
      vendedorNome: vendedorNome ? vendedorNome : '',
      statusConferencia: statusConferencia ? statusConferencia : '',
      tipoFatura: Array.isArray(tipoFatura) ? tipoFatura.join(',') : tipoFatura ? tipoFatura : 'PROCESSOS',
      naturezaFatura: naturezaFatura.join(','),
      funcaoParceiroSacadoNome: funcaoParceiroSacadoNome ? funcaoParceiroSacadoNome : '',
      size: size,
      page: page,
      sort: sort,
    };
    return this._http.get<Pageable>(`${environment.apiUrl}/api/financial/v1/faturas-tr`, { params });
  }

  getById(faturaTrId: number) {
    return this._http.get<FaturaTr>(`${environment.apiUrl}/api/financial/v1/faturas-tr/${faturaTrId}`);
  }

  getDistinctByProcesso(codigoProcesso: string) {
    const params = {
      codigoProcesso: codigoProcesso,
    };
    return this._http.get<FaturaTr[]>(`${environment.apiUrl}/api/financial/v1/faturas-tr/find-distinct-by-processo`, { params });
  }

  getUnificadasById(faturaTrId: number) {
    return this._http.get<FaturaTr[]>(`${environment.apiUrl}/api/financial/v1/faturas-tr/unificadas-by-id/${faturaTrId}`);
  }

  getFaturasProcessos(codigoProcesso: string) {
    return this._http.get<FaturaTr[]>(`${environment.apiUrl}/api/financial/v1/faturas-tr/faturas-process-by-id/${codigoProcesso}`);
  }

  create(fatura: FaturaTr) {
    return this._http.post<FaturaTr>(`${environment.apiUrl}/api/financial/v1/faturas-tr`, fatura);
  }

  createAdministrativa(fatura: FaturaTr) {
    return this._http.post<FaturaTr>(`${environment.apiUrl}/api/financial/v1/faturas-tr/administrativa`, fatura);
  }

  update(fatura: FaturaTr) {
    return this._http.put<FaturaTr>(`${environment.apiUrl}/api/financial/v1/faturas-tr/${fatura.faturaTrId}`, fatura);
  }

  updateUnificada(fatura: FaturaTr) {
    return this._http.put<FaturaTr>(`${environment.apiUrl}/api/financial/v1/faturas-tr/unificada/${fatura.faturaTrId}`, fatura);
  }

  updateHeader(fatura: FaturaTr) {
    return this._http.put<FaturaTr>(`${environment.apiUrl}/api/financial/v1/faturas-tr/header/${fatura.faturaTrId}`, fatura);
  }

  atualizarTaxaSpreadFatura(fatura: FaturaTr) {
    return this._http.post<FaturaTr>(`${environment.apiUrl}/api/financial/v1/faturas-tr/atualizacao-fatura-spread`, fatura);
  }

  atualizarTaxaSpreadFaturaDefault(faturaTrId: number) {
    return this._http.put<boolean>(`${environment.apiUrl}/api/financial/v1/faturas-tr/atualizacao-fatura-spread-automatica/${faturaTrId}`, null);
  }

  deleteById(faturaTrId: number) {
    return this._http.delete(`${environment.apiUrl}/api/financial/v1/faturas-tr/${faturaTrId}`);
  }

  enviarParaJuridico(fatura: FaturaTr) {
    return this._http.put<FaturaTr>(`${environment.apiUrl}/api/financial/v1/faturas-tr/enviar-juridico/${fatura.faturaTrId}`, fatura);
  }

  enviarParaExtraJudicial(fatura: FaturaTr) {
    return this._http.put<FaturaTr>(`${environment.apiUrl}/api/financial/v1/faturas-tr/enviar-tratamento-extrajudicial/${fatura.faturaTrId}`, null);
  }

  inserirImpostoRetido(fatura: FaturaTr) {
    return this._http.post<FaturaTr>(`${environment.apiUrl}/api/financial/v1/faturas-tr/imposto-retido`, fatura);
  }

  enviarCobranca(fatura: FaturaTr) {
    return this._http.post<FaturaTr>(`${environment.apiUrl}/api/financial/v1/faturas-tr/enviar-cobranca/${fatura.faturaTrId}`, fatura);
  }

  agruparFaturas(ids: number[]) {
    return this._http.post<FaturaTr>(`${environment.apiUrl}/api/financial/v1/faturas-tr/fatura-agrupadas`, ids);
  }

  desagruparFaturas(faturaDesagrupar: FaturaTrDesagrupar) {
    return this._http.post<FaturaTr>(`${environment.apiUrl}/api/financial/v1/faturas-tr/fatura-desagrupar`, faturaDesagrupar);
  }

  enviarPagamento(faturasSelecionadas: FaturaTr[]) {
    return this._http.post<FaturaTr>(`${environment.apiUrl}/api/financial/v1/faturas-tr/fatura-pagamento`, faturasSelecionadas);
  }

  enviarBaixa(faturasSelecionadas: FaturaTr[], dtBaixa: string, contaBancariaId: number) {
    return this._http.post<FaturaTr>(
      `${environment.apiUrl}/api/financial/v1/faturas-tr/enviar-baixa?dtBaixaCompensacao=${dtBaixa}&contaBancariaId=${contaBancariaId}`,
      faturasSelecionadas
    );
  }

  enviarBaixaParcial({
    faturasSelecionadas,
    dtBaixa,
    contaBancariaId,
    valor,
  }: {
    faturasSelecionadas: FaturaTr[];
    dtBaixa: string;
    contaBancariaId: number;
    valor: number;
  }) {
    return this._http.post<FaturaTr>(
      `${environment.apiUrl}/api/financial/v1/faturas-tr/enviar-baixa-parcial?dtBaixaCompensacao=${dtBaixa}&contaBancariaId=${contaBancariaId}&valor=${valor}`,
      faturasSelecionadas
    );
  }

  conferirDesconferir(faturaTrId: number, isConferido: boolean) {
    return this._http.put<boolean>(
      `${environment.apiUrl}/api/financial/v1/faturas-tr/conferir-desconferir?faturaTrId=${faturaTrId}&isConferido=${isConferido}`,
      {}
    );
  }

  findByCategoriaAndCentroCustos(categoriaFinanceiraId: number, centroCustoId: number, ano: string) {
    const params = {
      categoriaFinanceiraId: categoriaFinanceiraId,
      centroCustoId: centroCustoId,
      ano: ano,
    };

    return this._http.get<FaturaTr[]>(`${environment.apiUrl}/api/financial/v1/faturas-tr/find-by-categoria-and-centro-custo`, { params });
  }

  atualizarStatus(faturaTrId: number, status: any) {
    return this._http.put<FaturaTr>(
      `${environment.apiUrl}/api/financial/v1/faturas-tr/atualizar-status?faturaTrId=${faturaTrId}&status=${status}`,
      {}
    );
  }

  atualizarStatusDivergente(faturaTrId: number) {
    return this._http.put<FaturaTr>(`${environment.apiUrl}/api/financial/v1/faturas-tr/atualizar-status-divergente?faturaTrId=${faturaTrId}`, {});
  }

  separarCustos(separarCustos: SepararCustosTr) {
    return this._http.post<FaturaTr>(`${environment.apiUrl}/api/financial/v1/faturas-tr/separar-custos`, separarCustos);
  }

  rateioValores(rateioValores: RateioValoresTr) {
    return this._http.post<FaturaTr>(`${environment.apiUrl}/api/financial/v1/faturas-tr/rateio-valores`, rateioValores);
  }

  aprovaOuReprovaFaturaAdministrativa(faturaTrId: number, status: FaturaTrStatus) {
    return this._http.put<FaturaTr>(
      `${environment.apiUrl}/api/financial/v1/faturas-tr/aprova-reprova-fatura-budget?faturaTrId=${faturaTrId}&status=${status}`,
      {}
    );
  }

  getAllAudit(faturaTrId: number, page: number, email: string, dataInicial: Date, dataFinal: Date, tipoAuditoria: string) {
    let params = {
      size: 10,
      page: page,
      dataInicial: dataInicial ? this._timezoneService.utcToUserString(dataInicial) : '',
      dataFinal: dataFinal ? this._timezoneService.utcToUserString(dataFinal) : '',
      email: email ? email : '',
      tipoAuditoria: tipoAuditoria ? tipoAuditoria : '',
    };

    return this._http.get<Pageable>(`${environment.apiUrl}/api/financial/v1/fatura-tr-auditorias/${faturaTrId}`, { params: params });
  }

  findLucroEsperado(processoTrId: number) {
    return this._http.get<number>(`${environment.apiUrl}/api/financial/v1/faturas-tr/processo-lucro-esperado?processoTrId=${processoTrId}`);
  }

  findLucroAtual(processoTrId: number) {
    return this._http.get<number>(`${environment.apiUrl}/api/financial/v1/faturas-tr/find-lucro-atual?processoTrId=${processoTrId}`);
  }

  findPagamentoAgente(processoTrId: number) {
    return this._http.get<number>(`${environment.apiUrl}/api/financial/v1/faturas-tr/find-pagamento-agente?processoTrId=${processoTrId}`);
  }

  findAllFaturasByProcessoId(processoTrId: number) {
    return this._http.get<FaturaProcessoTrAbaResultado[]>(
      `${environment.apiUrl}/api/financial/v1/faturas-tr/find-all-faturas-by-processo-id?processoTrId=${processoTrId}`
    );
  }

  findDesencaixe(processoTrId: number) {
    return this._http.get<number>(`${environment.apiUrl}/api/financial/v1/faturas-tr/find-desencaixe?processoTrId=${processoTrId}`);
  }

  findAdiantamento(processoTrId: number) {
    return this._http.get<number>(`${environment.apiUrl}/api/financial/v1/faturas-tr/find-adiantamento?processoTrId=${processoTrId}`);
  }

  verificaFaturaQuitadaByProcesso(processoTrId: number) {
    return this._http.get<boolean>(`${environment.apiUrl}/api/financial/v1/faturas-tr/verifica-fatura-quitada-by-processo/${processoTrId}`);
  }

  atualizaAllContatosFatura(faturaTrId: number) {
    return this._http.put<boolean>(`${environment.apiUrl}/api/financial/v1/faturas-tr/atualiza-contatos/${faturaTrId}`, null);
  }

  getAuditComparacao(faturaTrId: number, rev: number) {
    return this._http.get<any[]>(`${environment.apiUrl}/api/financial/v1/fatura-tr-auditorias/comparacao-audit/${faturaTrId}?rev=${rev}`);
  }

  getAllParaRateioValores({ faturaNumero = '', processo = '', sacadoId = '', page = 0, sort = 'faturaTrId,desc' }) {
    const params = {
      faturaNumero: faturaNumero,
      processo: processo,
      sacadoId: sacadoId ? sacadoId : '',
      size: 500,
      page: page,
      sort: sort,
    };
    return this._http.get<Pageable>(`${environment.apiUrl}/api/financial/v1/faturas-tr/get-faturas-para-rateio-valores`, { params });
  }

  // TODO: fluxo Invoice não faz parte do Processo TR / UI FaturaTr — revisar se estes endpoints serão necessários
  // getFaturasInvoiceByProcessoId(processoTrId: number) {
  //   let params = { processoTrId: processoTrId };
  //   return this._http.get<FaturaTrInvoiceList[]>(`${environment.apiUrl}/api/financial/v1/faturas-tr/get-faturas-invoices`, { params: params });
  // }
  //
  // saveFaturasByProcessoInvoice(processoInvoice: ProcessoTrInvoiceFaturaCreate) {
  //   return this._http.post<FaturaTrInvoice>(`${environment.apiUrl}/api/financial/v1/faturas-tr/by-processo-invoice`, processoInvoice);
  // }
  //
  // verificaFaturasInvoiceQuitadasOuConferidas(processoTrId: number) {
  //   let params = { processoTrId: processoTrId };
  //   return this._http.get<boolean>(`${environment.apiUrl}/api/financial/v1/faturas-tr/verifica-faturas-invoices-soas-quitadas-conferidas`, {
  //     params: params,
  //   });
  // }

  verificaFaturaQuitadaSoaConferidaByProcessoId(processoTrId: number) {
    let params = {
      processoTrId: processoTrId,
    };

    return this._http.get<boolean>(`${environment.apiUrl}/api/financial/v1/faturas-tr/verifica-faturas-by-processo-id`, {
      params: params,
    });
  }

  conferirOperacionalmente(faturaTrId: number, conferir: boolean) {
    let body = {
      isConferidoOperacionalmente: conferir,
    };

    return this._http.put<boolean>(`${environment.apiUrl}/api/financial/v1/faturas-tr/is-conferido-operacionalmente/${faturaTrId}`, body);
  }

  getConferidoOperacionalmente(processoTrId: number) {
    return this._http.get<boolean>(`${environment.apiUrl}/api/financial/v1/faturas-tr/is-conferido-operacionalmente?processoTrId=${processoTrId}`);
  }

  getFuncaoParceiroById(faturaTrId: number) {
    return this._http.get<FaturaTr>(`${environment.apiUrl}/api/financial/v1/faturas-tr/get-funcao-parceiro-by-id/${faturaTrId}`);
  }

  verificaProcessoAgenteMesmoQueSacado(processoTrId: number, sacadoId: number) {
    return this._http.get<boolean>(`${environment.apiUrl}/api/financial/v1/faturas-tr/processo-agente-mesmo-sacado/${processoTrId}/${sacadoId}`);
  }
}
